import { createLink } from '@tanstack/react-router'
import { LucideIcon } from 'components/LucideIcon'
import {
  useTitle,
  useTypeDisplayName,
  useTypeMetadata,
} from 'features/dynamicOntology'
import React, { FC } from 'react'
import { createPrismSearchFromEntry } from 'routes/prism/-searchParams'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoEntry } from 'types'

const StyledLinkAnchorBase = styled.a`
  display: flex;
  padding: 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease-out;
  background: ${CantileverTheme.colors.grayscale['600']};
  border: 1px solid ${CantileverTheme.colors.gray['700']};
  text-align: left;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  text-decoration: none;
  color: inherit;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0;
    background: ${CantileverTheme.colors.gecko_brand['400']};
    border-radius: 0.5rem 0 0 0.5rem;
    transition: width 0.15s ease-out;
  }

  &:hover {
    border-color: ${CantileverTheme.colors.gray['600']};
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.06),
      0 4px 12px rgba(0, 0, 0, 0.04);

    &::before {
      width: 3px;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    background: ${CantileverTheme.colors.gray['300']};

    &::before {
      width: 3px;
    }
  }

  &:focus-visible {
    outline: 2px solid ${CantileverTheme.colors.gecko_brand['400']};
    outline-offset: 2px;
  }
`

const LinkItem = createLink(StyledLinkAnchorBase)

const LinkItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
`

const LinkItemIcon = styled.div<{ $typeColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$typeColor};
  margin-right: 1rem;
  opacity: 0.8;
`

const LinkItemBody = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
`

const LinkName = styled.div`
  font-weight: 600;
  color: ${CantileverTheme.colors.gray['500_base']};
  font-size: 0.875rem;
  line-height: 1.3;
  margin-bottom: 0.125rem;
`

const LinkType = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${CantileverTheme.colors.gray['500_base']};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
`

export interface ObjectLinkItemProps {
  link: GeckoEntry
  currentOrgId: string | undefined
  currentSiteId: string | undefined
}

export const ObjectLinkItem: FC<ObjectLinkItemProps> = ({
  link,
  currentOrgId,
  currentSiteId,
}) => {
  const title = useTitle(link)
  const typeName = useTypeDisplayName(link.type)
  const { iconName, typeColor } = useTypeMetadata(link.type)

  return (
    <LinkItem
      key={link.id}
      to='/prism'
      search={createPrismSearchFromEntry(link, {
        orgId: currentOrgId,
        siteId: currentSiteId,
      })}>
      <LinkItemBody>
        {iconName && (
          <LinkItemIcon $typeColor={typeColor}>
            <LucideIcon name={iconName} size={18} />
          </LinkItemIcon>
        )}
        <LinkItemContent>
          <LinkName>{title}</LinkName>
          <LinkType>{typeName}</LinkType>
        </LinkItemContent>
      </LinkItemBody>
    </LinkItem>
  )
}
